import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Button,
    TextField,
    Typography,
    Alert,
    Tabs,
    Tab,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Card,
    CardContent,
    Grid,
    Chip,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Tooltip,
    CircularProgress
} from '@mui/material';
import { Close as CloseIcon, CloudUpload as CloudUploadIcon, YouTube as YouTubeIcon, Add as AddIcon, MusicNote as MusicNoteIcon, Login as LoginIcon, Lock as LockIcon, Cloud as CloudIcon } from '@mui/icons-material';
import { AuditioBackendManager } from './AuditioBackend';
import { SessionManager } from './SessionManager';
import { cleanYouTubeUrl, validateYouTubeUrl, extractVideoId } from './utils/youtubeUrlUtils';
import { useAuth } from './contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { GoogleDriveService } from './services/GoogleDriveService';

type UploadTabsProps = {
    auditioBackendManager: AuditioBackendManager;
    audioFile: string;
    setAudioFile: React.Dispatch<React.SetStateAction<string>>;
    audioFileId: number;
    setAudioFileId: React.Dispatch<React.SetStateAction<number>>;
    renderTrigger: number;
    setRenderTrigger: React.Dispatch<React.SetStateAction<number>>;
    forceNoFilesView?: boolean;
    onFileSelect?: (fileId: number) => void;
    // Dialog-specific props
    isDialog?: boolean;
    open?: boolean;
    onClose?: () => void;
}

const UploadTabs: React.FC<UploadTabsProps> = ({
    auditioBackendManager,
    audioFile,
    setAudioFile,
    audioFileId,
    setAudioFileId,
    renderTrigger,
    setRenderTrigger,
    forceNoFilesView = false,
    onFileSelect,
    isDialog = false,
    open = false,
    onClose
}) => {
    const { user, firebaseUser } = useAuth();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(0);
    const [message, setMessage] = useState('');
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    
    // YouTube upload state
    const [youtubeUrl, setYoutubeUrl] = useState('');
    const [title, setTitle] = useState('');
    const [isUploadingYouTube, setIsUploadingYouTube] = useState(false);
    const [youtubeError, setYoutubeError] = useState<string | null>(null);
    const [youtubeSuccess, setYoutubeSuccess] = useState<string | null>(null);
    
    // Google Drive state
    const [isUploadingDrive, setIsUploadingDrive] = useState(false);
    const [driveError, setDriveError] = useState<string | null>(null);
    const [driveService] = useState(() => new GoogleDriveService());
    
    // Mobile detection
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isTouchDevice = 'ontouchstart' in window;

    const uploadFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        // Check if user is authenticated
        if (!user || !firebaseUser) {
            setUploadError('Please log in to upload files');
            return;
        }

        setUploading(true);
        setUploadError(null); // Clear any previous errors
        const file = e.target.files?.[0];
        if (!file) return;

        console.log('🚀 Upload started from upload tabs:', {
            fileName: file.name,
            fileSize: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
            fileType: file.type,
            backendManager: !!auditioBackendManager
        });

        setMessage("Uploading file...");

        try {
            console.log('📁 File ready for upload...');
            
            // Get Firebase ID token for authentication
            const idToken = await firebaseUser.getIdToken();
            
            // Upload the file using the AuditioBackendManager with authentication
            const uploadedFile = await auditioBackendManager.uploadFile(file, null, setMessage, idToken);
            console.log("✅ Upload result:", uploadedFile);
            console.log("🔍 Full uploadedFile object:", JSON.stringify(uploadedFile, null, 2));
            console.log("🔍 Setting audioFileId to:", uploadedFile.id);
            setMessage("Upload successful!");
            
            // Update UI state
            setAudioFile(uploadedFile.fileName);
            setAudioFileId(uploadedFile.id);
            setRenderTrigger(prev => prev + 1);
            
            // Track uploaded file in session
            console.log('📝 Adding uploaded file to session:', {
                id: uploadedFile.id,
                fileName: uploadedFile.fileName,
                isUserUploaded: true,
                uploadTime: uploadedFile.uploadTime.getTime()
            });
            SessionManager.addRecentFile(uploadedFile.id, uploadedFile.fileName, true, uploadedFile.uploadTime.getTime(), user.id);
            
            // Clear the file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            
            // Close dialog if in dialog mode
            if (isDialog && onClose) {
                onClose();
            }
            
            // Navigate immediately after upload
            console.log('🚀 Navigating to:', `/${uploadedFile.id}`);
            window.location.href = `/${uploadedFile.id}`;
        } catch (err) {
            console.error("❌ Upload error:", err);
            console.error("❌ Error details:", {
                message: err instanceof Error ? err.message : 'Unknown error',
                stack: err instanceof Error ? err.stack : undefined,
                backendManager: !!auditioBackendManager
            });
            
            const errorMessage = err instanceof Error ? err.message : 'Unknown error';
            setMessage(`Error uploading file: ${errorMessage}`);
            setUploadError(errorMessage); // Set the error state for display
        } finally {
            setUploading(false);
        }
    }

    // Utility function to clean YouTube URLs by removing problematic parameters
    // const cleanYouTubeUrl = (url: string): string => {
    //     try {
    //         const urlObj = new URL(url);
            
    //         // Remove problematic parameters that can cause errors
    //         const paramsToRemove = ['start_radio', 'list', 'feature', 'app', 'si', 'ab_channel'];
    //         paramsToRemove.forEach(param => {
    //             urlObj.searchParams.delete(param);
    //         });
            
    //         // Ensure we have the essential video ID parameter
    //         if (!urlObj.searchParams.has('v')) {
    //             // If it's a youtu.be URL, convert it to youtube.com format
    //             if (urlObj.hostname === 'youtu.be') {
    //                 const videoId = urlObj.pathname.slice(1); // Remove leading slash
    //                 urlObj.hostname = 'www.youtube.com';
    //                 urlObj.pathname = '/watch';
    //                 urlObj.searchParams.set('v', videoId);
    //             }
    //         }
            
    //         return urlObj.toString();
    //     } catch (error) {
    //         // If URL parsing fails, return the original URL
    //         console.warn('Failed to parse YouTube URL for cleaning:', error);
    //         return url;
    //     }
    // };

    // const validateYouTubeUrl = (url: string): boolean => {
    //     // Clean the URL first, then validate
    //     const cleanedUrl = cleanYouTubeUrl(url);
    //     const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    //     return youtubeRegex.test(cleanedUrl);
    // };

    // const extractVideoId = (url: string): string | null => {
    //     // Clean the URL first, then extract video ID
    //     const cleanedUrl = cleanYouTubeUrl(url);
    //     const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    //     const match = cleanedUrl.match(youtubeRegex);
    //     return match ? match[4] : null;
    // };

    const handleYouTubeUpload = async () => {
        // Check if user is authenticated
        if (!user || !firebaseUser) {
            setYoutubeError('Please log in to upload YouTube videos');
            return;
        }

        if (!youtubeUrl.trim()) {
            setYoutubeError('Please enter a YouTube URL');
            return;
        }

        if (!validateYouTubeUrl(youtubeUrl)) {
            setYoutubeError('Please enter a valid YouTube URL');
            return;
        }

        setIsUploadingYouTube(true);
        setYoutubeError(null);
        setYoutubeSuccess(null);

        try {
            // Clean the URL before sending to backend
            const cleanedUrl = cleanYouTubeUrl(youtubeUrl.trim());
            console.log('🧹 Frontend - Original URL:', youtubeUrl);
            console.log('🧹 Frontend - Cleaned URL:', cleanedUrl);

            // Get Firebase ID token for authentication
            const idToken = await firebaseUser.getIdToken();

            const response = await fetch(`${auditioBackendManager.getBaseUrl()}/upload-youtube`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${idToken}`
                },
                body: JSON.stringify({
                    youtubeUrl: cleanedUrl,
                    title: title.trim() || undefined
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to upload YouTube video');
            }

            const result = await response.json();
            
            setYoutubeSuccess('YouTube video uploaded successfully!');
            setYoutubeUrl('');
            setTitle('');
            
            // Track uploaded YouTube file in session as user-uploaded
            console.log('📝 Adding uploaded YouTube file to session:', {
                id: result.id,
                fileName: result.fileName,
                isUserUploaded: true,
                uploadTime: result.uploadTime
            });
            SessionManager.addRecentFile(result.id, result.fileName, true, result.uploadTime, user.id);
            
            // Close dialog if in dialog mode
            if (isDialog && onClose) {
                onClose();
            }
            
            // Navigate immediately after upload
            console.log('🚀 Navigating to:', `/${result.id}`);
            window.location.href = `/${result.id}`;
        } catch (err) {
            console.error('Error uploading YouTube video:', err);
            setYoutubeError(err instanceof Error ? err.message : 'Failed to upload YouTube video');
        } finally {
            setIsUploadingYouTube(false);
        }
    };

    const handleKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            if (activeTab === 0) {
                // File upload tab - do nothing on Enter
            } else {
                handleYouTubeUpload();
            }
        }
    };

    const handleClose = () => {
        if (!uploading && !isUploadingYouTube) {
            setMessage('');
            setYoutubeError(null);
            setYoutubeSuccess(null);
            if (onClose) {
                onClose();
            }
        }
    };

    const renderContent = () => {
        // Show login prompt when not authenticated
        if (!user) {
            return (
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    p={isDialog ? 3 : 4}
                    textAlign="center"
                >
                    <LockIcon sx={{ width: 64, height: 64, color: 'var(--primary)', mb: 2 }} />
                    <Typography variant="h5" sx={{ 
                        fontFamily: 'Soria, serif',
                        color: 'var(--text-color)', 
                        fontWeight: 600,
                        mb: 2
                    }}>
                        Login Required
                    </Typography>
                    <Typography variant="body1" sx={{ 
                        color: 'var(--text-color)', 
                        mb: 3, 
                        opacity: 0.8,
                        fontSize: '1rem',
                        lineHeight: 1.6
                    }}>
                        Please log in to upload audio files or create tracks from YouTube recordings.
                    </Typography>
                    <Button
                        variant="contained"
                        fullWidth
                        startIcon={<LoginIcon />}
                        onClick={() => {
                            navigate('/login');
                            if (isDialog && onClose) {
                                onClose();
                            }
                        }}
                        sx={{
                            backgroundColor: '#f9967e !important',
                            color: 'white !important',
                            boxShadow: '0 4px 12px rgba(249, 150, 126, 0.3) !important',
                            borderRadius: 'var(--radius-md) !important',
                            fontWeight: 500,
                            textTransform: 'none',
                            border: 'none',
                            fontSize: '1.1rem',
                            padding: '12px 32px',
                            background: 'linear-gradient(135deg, #f9967e, #fcb9aa) !important',
                            width: '100%',
                            maxWidth: '400px',
                            '&:hover': {
                                backgroundColor: '#f9967e !important',
                                background: 'linear-gradient(135deg, #fcb9aa, #f9967e) !important',
                                transform: 'translateY(-2px) scale(1.02)',
                                boxShadow: '0 8px 20px rgba(249, 150, 126, 0.5) !important',
                            }
                        }}
                    >
                        Log In
                    </Button>
                </Box>
            );
        }

        return (
            <>
                {isDialog && (
                    <DialogTitle sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        pb: 1
                    }}>
                        <Typography variant="h5" sx={{ 
                            fontFamily: 'Soria, serif',
                            color: 'var(--text-color)', 
                            fontWeight: 600 
                        }}>
                            Add New Track
                        </Typography>
                        <IconButton 
                            onClick={handleClose}
                            disabled={uploading || isUploadingYouTube}
                            sx={{ color: 'var(--text-color)' }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                )}
                
                <Box sx={isDialog ? { pb: 2 } : {}}>
                    <Tabs 
                        value={activeTab} 
                        onChange={(_, newValue) => setActiveTab(newValue)}
                        sx={{ mb: 3 }}
                    >
                        <Tab label="Upload File" />
                        <Tab label="YouTube Link" />
                    </Tabs>
                
                {activeTab === 0 && (
                    <Box
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center"
                        p={isDialog ? 3 : 4}
                        textAlign="center"
                    >
                        <CloudUploadIcon sx={{ width: 64, height: 64, color: 'var(--primary)', mb: 2 }} className="home-music-note" />
                        <Typography variant="body1" sx={{ 
                            color: 'var(--text-color)', 
                            mb: 3, 
                            opacity: 0.8,
                            fontSize: '1rem',
                            lineHeight: 1.6
                        }}>
                            Select an audio file to upload. Supported formats include MP3, WAV, M4A, and more.
                        </Typography>
                        
                        {/* File Size Limit Info */}
                        <Alert 
                            severity="info" 
                            sx={{ 
                                mb: 3,
                                '& .MuiAlert-message': {
                                    color: '#1976d2 !important'
                                }
                            }}
                        >
                            <strong>File Size Limit:</strong> Maximum file size after conversion is 32MB.
                        </Alert>
                        
                        {uploading && (
                            <Box
                                display="flex"
                                flexDirection="column"
                                justifyContent="center"
                                alignItems="center"
                                mb={2}
                            >
                                <CircularProgress sx={{ color: 'var(--primary)' }} />
                                <Typography variant="body2" sx={{ 
                                    color: 'var(--text-color)', 
                                    mt: 1,
                                    fontSize: '0.9rem'
                                }}>
                                    {message}
                                </Typography>
                            </Box>
                        )}

                        <Button
                            variant="contained"
                            fullWidth
                            startIcon={<CloudUploadIcon />}
                            onClick={() => {
                                console.log('🔘 Upload button clicked!');
                                console.log('🔘 Auth state at click:', { 
                                    user: !!user, 
                                    firebaseUser: !!firebaseUser,
                                    userEmail: user?.email,
                                    firebaseEmail: firebaseUser?.email
                                });
                                
                                // Check authentication before allowing file selection
                                if (!user || !firebaseUser) {
                                    console.log('❌ User not authenticated, showing error');
                                    setUploadError('Please log in to upload files');
                                    return;
                                }
                                
                                console.log('✅ User authenticated, proceeding with file selection');
                                console.log('🔘 Device info:', { isMobile, isTouchDevice, userAgent: navigator.userAgent });
                                console.log('🔘 fileInputRef.current:', fileInputRef.current);
                                if (fileInputRef.current) {
                                    console.log('🔘 Triggering file input click...');
                                    fileInputRef.current.click();
                                } else {
                                    console.error('❌ fileInputRef.current is null!');
                                }
                            }}
                            disabled={uploading}
                            sx={{
                                backgroundColor: '#f9967e !important',
                                color: 'white !important',
                                boxShadow: '0 4px 12px rgba(249, 150, 126, 0.3) !important',
                                borderRadius: 'var(--radius-md) !important',
                                fontWeight: 500,
                                textTransform: 'none',
                                border: 'none',
                                fontSize: '1.1rem',
                                padding: '12px 32px',
                                background: 'linear-gradient(135deg, #f9967e, #f9967e) !important',
                                width: '100%',
                                '&:hover': {
                                    backgroundColor: '#f9967e !important',
                                    background: 'linear-gradient(135deg, #f9967e, #f9967e) !important',
                                    transform: 'translateY(-2px) scale(1.02)',
                                    boxShadow: '0 8px 20px rgba(249, 150, 126, 0.5) !important',
                                },
                                '&:disabled': {
                                    backgroundColor: 'rgba(249, 150, 126, 0.5) !important',
                                    background: 'linear-gradient(135deg, rgba(249, 150, 126, 0.5), rgba(249, 150, 126, 0.5)) !important',
                                    color: 'rgba(255, 255, 255, 0.7) !important',
                                    transform: 'none',
                                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1) !important',
                                }
                            }}
                        >
                            {uploading ? 'Uploading...' : 'Choose File'}
                        </Button>

                        {/* Google Drive Button */}
                        <Button
                            variant="outlined"
                            fullWidth
                            startIcon={
                                <Box
                                    component="img"
                                    src="https://www.gstatic.com/images/branding/product/1x/drive_48dp.png"
                                    alt="Google Drive"
                                    sx={{ width: 20, height: 20, mr: 1 }}
                                />
                            }
                            onClick={async () => {
                                // Check authentication before allowing Drive selection
                                if (!user || !firebaseUser) {
                                    setDriveError('Please log in to select files from Google Drive');
                                    return;
                                }

                                setIsUploadingDrive(true);
                                setDriveError(null);

                                try {
                                    // Show Google Picker
                                    const selectedFile = await driveService.showPicker();
                                    
                                    if (!selectedFile) {
                                        setIsUploadingDrive(false);
                                        return; // User cancelled
                                    }

                                    console.log('📥 Google Drive file selected:', selectedFile);

                                    // Validate that we have a valid access token for this file
                                    // This helps ensure the file can be accessed later
                                    try {
                                        const accessToken = await driveService.getAccessToken();
                                        if (!accessToken) {
                                            throw new Error('No access token available');
                                        }
                                        console.log('✅ Access token validated for selected file');
                                        // Token is valid at this point since getAccessToken() ensures it's not expired
                                    } catch (tokenError) {
                                        console.warn('⚠️ Could not validate access token:', tokenError);
                                        // Don't block upload, but warn user
                                        const errorMsg = tokenError instanceof Error ? tokenError.message : String(tokenError);
                                        if (!errorMsg.includes('cancelled')) {
                                            console.warn('⚠️ Token validation failed, file may not be accessible later if token expires');
                                        }
                                    }

                                    // Get Firebase ID token for authentication
                                    const idToken = await firebaseUser.getIdToken();

                                    // Upload Drive file metadata to backend
                                    const response = await fetch(`${auditioBackendManager.getBaseUrl()}/upload-drive`, {
                                        method: 'POST',
                                        headers: {
                                            'Content-Type': 'application/json',
                                            'Authorization': `Bearer ${idToken}`
                                        },
                                        body: JSON.stringify({
                                            driveFileId: selectedFile.id,
                                            driveFileUrl: selectedFile.webViewLink || selectedFile.webContentLink,
                                            fileName: selectedFile.name,
                                            mimeType: selectedFile.mimeType
                                        }),
                                    });

                                    if (!response.ok) {
                                        const errorData = await response.json();
                                        const errorMessage = errorData.error || errorData.details || 'Failed to upload Google Drive file';
                                        console.error('❌ Backend error response:', {
                                            status: response.status,
                                            statusText: response.statusText,
                                            error: errorData
                                        });
                                        throw new Error(errorMessage);
                                    }

                                    const result = await response.json();
                                    console.log('✅ Google Drive file uploaded successfully:', result);

                                    // Update UI state
                                    setAudioFile(result.fileName);
                                    setAudioFileId(result.id);
                                    setRenderTrigger(prev => prev + 1);

                                    // Track uploaded Drive file in session
                                    SessionManager.addRecentFile(result.id, result.fileName, true, result.uploadTime, user.id);

                                    // Close dialog if in dialog mode
                                    if (isDialog && onClose) {
                                        onClose();
                                    }

                                    // Navigate immediately after upload
                                    console.log('🚀 Navigating to:', `/${result.id}`);
                                    navigate(`/${result.id}`, { replace: true });
                                } catch (err) {
                                    console.error('❌ Error uploading Google Drive file:', err);
                                    const errorMessage = err instanceof Error 
                                        ? `${err.message}${err.stack ? '\n\n' + err.stack : ''}` 
                                        : 'Failed to upload Google Drive file';
                                    setDriveError(errorMessage);
                                } finally {
                                    setIsUploadingDrive(false);
                                }
                            }}
                            disabled={uploading || isUploadingDrive}
                            sx={{
                                mt: 2,
                                backgroundColor: 'white !important',
                                color: 'var(--text-color) !important',
                                border: '1px solid #e0e0e0 !important',
                                borderRadius: 'var(--radius-md) !important',
                                fontWeight: 500,
                                textTransform: 'none',
                                fontSize: '1rem',
                                padding: '12px 32px',
                                width: '100%',
                                '&:hover': {
                                    backgroundColor: '#f5f5f5 !important',
                                    border: '1px solid #bdbdbd !important',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1) !important',
                                },
                                '&:disabled': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.05) !important',
                                    color: 'rgba(0, 0, 0, 0.3) !important',
                                    border: '1px solid rgba(0, 0, 0, 0.1) !important',
                                    transform: 'none',
                                    boxShadow: 'none !important',
                                }
                            }}
                        >
                            {isUploadingDrive ? 'Selecting...' : 'Select from Google Drive'}
                        </Button>

                        {/* Drive Error Display */}
                        {driveError && (
                            <Alert 
                                severity="error" 
                                sx={{ 
                                    mt: 2,
                                    '& .MuiAlert-message': {
                                        color: '#d32f2f !important'
                                    }
                                }}
                                onClose={() => setDriveError(null)}
                            >
                                {driveError}
                            </Alert>
                        )}
                        
                        {/* Error Display */}
                        {uploadError && (
                            <Alert 
                                severity="error" 
                                sx={{ 
                                    mt: 2,
                                    '& .MuiAlert-message': {
                                        color: '#d32f2f !important'
                                    }
                                }}
                                onClose={() => setUploadError(null)}
                            >
                                {uploadError}
                            </Alert>
                        )}
                        
                        {user && (
                            <input 
                                type="file" 
                                accept=".mp3,.m4a,.wav,.aac,.ogg,.flac,.wma,.aiff,.m4b,.m4p,.3gp,.amr,.opus,audio/mpeg,audio/mp4,audio/wav,audio/aac,audio/ogg,audio/flac,audio/x-ms-wma,audio/aiff,audio/3gpp,audio/amr,audio/opus,audio/*"
                                multiple={false}
                                style={{ display: 'none' }}
                                onChange={(e) => {
                                    console.log('📁 File input change event triggered!');
                                    console.log('📁 Device info:', { isMobile, isTouchDevice });
                                    console.log('📁 Files selected:', e.target.files?.length || 0);
                                    console.log('📁 File input accept attribute:', e.target.accept);
                                    if (e.target.files?.[0]) {
                                        console.log('📁 Selected file:', {
                                            name: e.target.files[0].name,
                                            type: e.target.files[0].type,
                                            size: e.target.files[0].size,
                                            lastModified: e.target.files[0].lastModified
                                        });
                                    }
                                    uploadFile(e);
                                }} 
                                ref={fileInputRef}
                            />
                        )}
                    </Box>
                )}
                
                {activeTab === 1 && (
                    <Box sx={{ p: isDialog ? 3 : 4 }}>
                        <Typography variant="h6" sx={{ 
                            fontFamily: 'Soria, serif',
                            color: 'var(--text-color)', 
                            fontWeight: 600,
                            mb: 2,
                            textAlign: isDialog ? 'left' : 'center'
                        }}>
                            Add YouTube Video
                        </Typography>
                        
                        <Typography variant="body1" sx={{ 
                            color: 'var(--text-color)', 
                            mb: 3, 
                            opacity: 0.8,
                            fontSize: '1rem',
                            lineHeight: 1.6,
                            textAlign: isDialog ? 'left' : 'center'
                        }}>
                            Enter a YouTube URL to add it to your collection. You can then create snippets and loop specific segments.
                        </Typography>

                        {youtubeError && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {youtubeError}
                            </Alert>
                        )}

                        {youtubeSuccess && (
                            <Alert severity="success" sx={{ mb: 2 }}>
                                {youtubeSuccess}
                            </Alert>
                        )}

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                            <TextField
                                fullWidth
                                label="YouTube URL"
                                placeholder="https://www.youtube.com/watch?v=..."
                                value={youtubeUrl}
                                onChange={(e) => setYoutubeUrl(e.target.value)}
                                onKeyPress={handleKeyPress}
                                disabled={isUploadingYouTube}
                                helperText="Enter a valid YouTube video URL"
                            />

                            <TextField
                                fullWidth
                                label="Video Title (Optional)"
                                placeholder="Enter a custom title for this video"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                onKeyPress={handleKeyPress}
                                disabled={isUploadingYouTube}
                                helperText="Leave empty to use the original video title"
                            />

                            {youtubeUrl && validateYouTubeUrl(youtubeUrl) && (
                                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Video ID: {extractVideoId(youtubeUrl)}
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        <Button
                            variant="contained"
                            fullWidth
                            startIcon={isUploadingYouTube ? <CircularProgress size={20} /> : <YouTubeIcon />}
                            onClick={handleYouTubeUpload}
                            disabled={isUploadingYouTube || !youtubeUrl.trim()}
                            sx={{
                                backgroundColor: '#f9967e !important',
                                color: 'white !important',
                                boxShadow: '0 4px 12px rgba(249, 150, 126, 0.3) !important',
                                borderRadius: 'var(--radius-md) !important',
                                fontWeight: 500,
                                textTransform: 'none',
                                border: 'none',
                                fontSize: '1.1rem',
                                padding: '12px 32px',
                                background: 'linear-gradient(135deg, #f9967e, #f9967e) !important',
                                width: '100%',
                                '&:hover': {
                                    backgroundColor: '#f9967e !important',
                                    background: 'linear-gradient(135deg, #f9967e, #f9967e) !important',
                                    transform: 'translateY(-2px) scale(1.02)',
                                    boxShadow: '0 8px 20px rgba(249, 150, 126, 0.5) !important',
                                },
                                '&:disabled': {
                                    backgroundColor: 'rgba(249, 150, 126, 0.5) !important',
                                    background: 'linear-gradient(135deg, rgba(249, 150, 126, 0.5), rgba(249, 150, 126, 0.5)) !important',
                                    color: 'rgba(255, 255, 255, 0.7) !important',
                                    transform: 'none',
                                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1) !important',
                                }
                            }}
                        >
                            {isUploadingYouTube ? 'Uploading...' : 'Add YouTube Video'}
                        </Button>
                    </Box>
                )}
            </Box>
            </>
        );
    };

    // If not forceNoFilesView, return empty content (this component is only for home page)
    if (!forceNoFilesView && !isDialog) {
        return null;
    }

    if (isDialog) {
        return (
            <Dialog 
                open={open} 
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '20px',
                        background: 'white',
                        boxShadow: '0 10px 30px rgba(85, 203, 205, 0.1)',
                    }
                }}
            >
                <DialogContent>
                    {renderContent()}
                </DialogContent>
            </Dialog>
        );
    }

    // Standalone component for home page
    return (
        <Box width="100%">
            <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                width="100%"
            >
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    p={4}
                    textAlign="center"
                    position="relative"
                    sx={{
                        background: 'white',
                        borderRadius: '20px',
                        boxShadow: '0 10px 30px rgba(85, 203, 205, 0.1)',
                        padding: '3rem',
                        maxWidth: '800px',
                        width: '100%',
                    }}
                >
                    <MusicNoteIcon sx={{ width: 64, height: 64, color: 'var(--primary)', mb: 2 }} className="home-music-note" />
                    <Typography variant="h4" sx={{ 
                        fontFamily: 'Soria, serif',
                        color: 'var(--text-color)', 
                        textAlign: 'center',
                        mb: 2
                    }}>
                        Welcome to Auditio
                    </Typography>
                    {!user ? (
                        <>
                            <Typography variant="body1" sx={{ 
                                color: 'var(--text-color)', 
                                mb: 3, 
                                opacity: 0.8,
                                fontSize: '1rem',
                                lineHeight: 1.6,
                                textAlign: 'center'
                            }}>
                                Please log in to upload audio files or create tracks from YouTube recordings.
                            </Typography>
                            {renderContent()}
                        </>
                    ) : (
                        <>
                            <Typography variant="body1" sx={{ 
                                color: 'var(--text-color)', 
                                mb: 3, 
                                opacity: 0.8,
                                fontSize: '1rem',
                                lineHeight: 1.6,
                                textAlign: isDialog ? 'left' : 'center'
                            }}>
                                Upload an audio file or add a YouTube video to get started. You can then create snippets and loop specific segments.
                            </Typography>
                            {renderContent()}
                        </>
                    )}
                </Box>
            </Box>
        </Box>
    );
}

export default UploadTabs; 